import React from 'react';
import { FaCalendarAlt, FaStar, FaTrashAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';

import { AiArticle } from '../../types/aiArticle';
import { getFirstArticleImageUrl } from '../../utils/imageUtils';
import { richTextToString } from '../../utils/richTextUtils';
import { ArticleImage } from '../media';

interface FavoriteArticleCardProps {
  article: AiArticle;
  onRemove: (favoriteId: string, itemName: string) => void;
  favoriteId: string;
  removingId: string | null;
}

const FavoriteArticleCard: React.FC<FavoriteArticleCardProps> = ({
  article,
  onRemove,
  favoriteId,
  removingId,
}) => {
  // 日付をフォーマットする関数
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const firstImageUrl = getFirstArticleImageUrl(article.images);
  const summary = article.summary ? richTextToString(article.summary) : '';

  return (
    <article className="bg-amber-50 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300">
      {/* 記事画像 */}
      <Link to={`/news/${article.documentId}`} className="block aspect-video overflow-hidden">
        <div className="relative">
          <ArticleImage
            src={firstImageUrl}
            alt={article.title}
            className="hover:scale-110 transition-transform duration-300"
            containerClassName="w-full h-full"
          />
          {/* お気に入りアイコン */}
          <div className="absolute top-2 right-2">
            <FaStar className="text-yellow-500 text-xl drop-shadow-md" />
          </div>
        </div>
      </Link>

      <div className="p-4 sm:p-6">
        {/* タイトル */}
        <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2 sm:mb-3 line-clamp-2 leading-tight">
          {article.title}
        </h3>

        {/* 要約 */}
        {summary && (
          <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-3 leading-relaxed">
            {summary}
          </p>
        )}

        {/* メイン生物 */}
        {article.main_creature && (
          <div className="text-xs text-green-600 mb-2 font-medium truncate">
            🦕 {article.main_creature}
          </div>
        )}

        {/* メタ情報 */}
        <div className="flex items-center text-xs text-gray-500 mb-3 sm:mb-4">
          <FaCalendarAlt className="mr-1 sm:mr-2 flex-shrink-0" />
          <span className="truncate">
            {formatDate(article.evaluated_at || article.publishedAt)}
          </span>
        </div>

        {/* 続きを読むボタン */}
        <Link
          to={`/news/${article.documentId}`}
          className="flex items-center bg-green-600 text-white px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium hover:bg-green-700 transition-colors min-h-[36px] mb-4"
        >
          続きを読む
        </Link>

        {/* お気に入り削除ボタン */}
        <button
          onClick={() => onRemove(favoriteId, article.title)}
          disabled={removingId === favoriteId}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {removingId === favoriteId ? (
            <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
          ) : (
            <FaTrashAlt className="w-4 h-4" />
          )}
          <span className="text-sm font-medium">
            {removingId === favoriteId ? '削除中...' : 'お気に入りから削除'}
          </span>
        </button>
      </div>
    </article>
  );
};

export default FavoriteArticleCard;
